import { useFavoriteBuses } from "contexts/FavoriteBusesContext/FavoriteBusesContext";
import useMapViewData from "contexts/MapViewDataContext/hooks/useMapViewData";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Bus, BusId, decryptBusId } from "types/busType";
import { areColocated } from "types/coordinatesAndZoom";
import { QUERY_PARAMS_FOR_MAP_VIEW } from "types/queryParams";
import {
    getMaybeSingleSelectedBus,
    SelectedBusesAtLocation
} from "types/selectedBusesAtLocation";
import { SelectedFacility } from "types/selectedFacility";
import { SelectedBusesContextProps } from "./SelectedBusesContext";

const useCreateSelectedBuses = (): SelectedBusesContextProps => {
    const [queryParams] = useSearchParams();
    const { scopedBuses } = useMapViewData();
    const { favorites, favoritesFilterOn } = useFavoriteBuses();

    const [selectedBusesAtLocation, setSelectedBusesAtLocation] =
        useState<SelectedBusesAtLocation | null>(
            maybeGetSelectedBusIdFromQueryParams({ queryParams })
        );
    const [selectedFacility, setSelectedFacility] =
        useState<SelectedFacility | null>(null);

    const selectSingleBus = (busId: BusId) => {
        setSelectedBusesAtLocation(
            getMaybeSingleSelectedBus({ selectedBusId: busId })
        );
        setSelectedFacility(null);
    };

    const selectMultipleBuses = ({
        selectedBusId,
        busIds
    }: {
        selectedBusId: BusId;
        busIds: BusId[];
    }) => {
        setSelectedBusesAtLocation({
            selectedBusId,
            busesAtLocation: busIds
        });
        setSelectedFacility(null);
    };

    const updateSelectedBusId = (busId: BusId) => {
        if (selectedBusesAtLocation) {
            setSelectedBusesAtLocation({
                ...selectedBusesAtLocation,
                selectedBusId: busId
            });
        }
        setSelectedFacility(null);
    };

    const clearSelectedBuses = () => {
        setSelectedBusesAtLocation(null);
        setSelectedFacility(null);
    };

    const selectLineTap = (facility: SelectedFacility) => {
        const { facilityId, fromBusId, toBusId } = facility;
        setSelectedBusesAtLocation(null);
        setSelectedFacility({ facilityId, fromBusId, toBusId });
    };

    // Non-map selections (search, sidebar, etc) only carry the selected
    // bus, so we add the others co-located with it.
    const selectedBusesAtLocationWithColocated = useMemo(() => {
        if (selectedBusesAtLocation === null) {
            return null;
        }
        const { selectedBusId, busesAtLocation } = selectedBusesAtLocation;
        const maybeSelectedBus = scopedBuses.find(
            ({ bus }) => bus.id === selectedBusId
        )?.bus;
        if (maybeSelectedBus === undefined) {
            return selectedBusesAtLocation;
        }
        // If there's already a list of buses, we don't want to re-calculate
        // colocation as a user pages through, since colocation isn't transitive.
        // Eg. if A-B-C each 1m apart on a line, if a user switched to A then C would disappear.
        const isAtLocation = (bus: Bus) =>
            busesAtLocation.length === 1
                ? areColocated(bus, maybeSelectedBus)
                : busesAtLocation.includes(bus.id);
        // The favorites filter hides non-favorites, so never page to one.
        const isOnMap = (bus: Bus) =>
            !favoritesFilterOn ||
            favorites.includes(bus.niraBusId) ||
            bus.id === selectedBusId;
        return {
            selectedBusId,
            busesAtLocation: scopedBuses
                .filter(({ bus }) => isAtLocation(bus) && isOnMap(bus))
                .map(({ bus }) => bus.id)
        };
    }, [selectedBusesAtLocation, scopedBuses, favorites, favoritesFilterOn]);

    const value = useMemo(
        () => ({
            selectedBusesAtLocation: selectedBusesAtLocationWithColocated,
            selectedFacility,
            selectSingleBus,
            selectMultipleBuses,
            updateSelectedBusId,
            clearSelectedBuses,
            selectLineTap
        }),
        [
            selectedBusesAtLocation,
            selectedBusesAtLocationWithColocated,
            selectedFacility
        ]
    );

    return value;
};

const maybeGetSelectedBusIdFromQueryParams = ({
    queryParams
}: {
    queryParams: URLSearchParams;
}): SelectedBusesAtLocation | null => {
    const maybeSelectedBusId = queryParams.get(
        QUERY_PARAMS_FOR_MAP_VIEW.selectedBusId
    );
    const maybeEncryptedSelectedBusId = queryParams.get(
        QUERY_PARAMS_FOR_MAP_VIEW.encryptedSelectedBusId
    );

    if (maybeSelectedBusId !== null) {
        return getMaybeSingleSelectedBus({
            selectedBusId: maybeSelectedBusId
        });
    } else if (maybeEncryptedSelectedBusId !== null) {
        const decryptedBusId = decryptBusId(maybeEncryptedSelectedBusId);
        return getMaybeSingleSelectedBus({
            selectedBusId: decryptedBusId
        });
    }
    return null;
};

export { useCreateSelectedBuses };
